import React, { useState, useEffect, useContext } from 'react'
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  ListGroup,
  ListGroupItem,
  Spinner
} from 'reactstrap'
import { Link } from 'react-router-dom'
import { getQueuedConversations } from '../../../helpers/chat_helper'
import { useAgentSocket } from '../../../hooks/useAgentSocket'
import { DataContext } from '../../../context/dataContext'
import moment from 'moment'

const QueueView = () => {
  const { token } = useContext(DataContext)
  const authToken = token || localStorage.getItem('token')
  const { takeConversation, isConnected } = useAgentSocket(authToken)
  
  const [queuedChats, setQueuedChats] = useState([])
  const [loading, setLoading] = useState(true)
  const [takingId, setTakingId] = useState(null)

  useEffect(() => {
    loadQueuedConversations()
    const interval = setInterval(loadQueuedConversations, 10000) // Refresh every 10s
    return () => clearInterval(interval)
  }, [])

  const loadQueuedConversations = async () => {
    try {
      setLoading(true)
      const response = await getQueuedConversations()
      const data = response?.data?.data || response?.data || []
      setQueuedChats(data)
    } catch (error) {
      console.error('Error loading queued conversations:', error)
      setQueuedChats([])
    } finally {
      setLoading(false)
    }
  }

  const handleTakeConversation = (conversationId) => {
    setTakingId(conversationId)
    takeConversation(conversationId, (response) => {
      if (response.success) {
        alert('Conversation assigned to you!')
        setQueuedChats(prev => prev.filter(c => c.id !== conversationId))
      } else {
        alert('Failed to take conversation: ' + (response.error || 'Unknown error'))
      }
      setTakingId(null)
    })
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <h4>Conversation Queue</h4>
            <p className="text-muted">Unassigned conversations waiting for an agent</p>
          </Col>
          <Col className="text-end">
            <Badge color={isConnected ? 'success' : 'danger'} className="me-2">
              {isConnected ? '● Online' : '● Offline'}
            </Badge>
            <Link to="/chat">
              <Button color="primary" size="sm">
                <i className="mdi mdi-chat"></i> Back to Chat
              </Button>
            </Link>
          </Col>
        </Row>

        <Row>
          <Col>
            <Card>
              <CardBody>
                {loading ? (
                  <div className="text-center py-4">
                    <Spinner color="primary" />
                    <p className="mt-2">Loading queue...</p>
                  </div>
                ) : queuedChats.length > 0 ? (
                  <>
                    <div className="mb-3">
                      <Badge color="warning" className="me-2">
                        {queuedChats.length} waiting
                      </Badge>
                      <Button 
                        color="link" 
                        size="sm" 
                        onClick={loadQueuedConversations}
                        disabled={loading}
                      >
                        <i className="mdi mdi-refresh"></i> Refresh
                      </Button>
                    </div>
                    <ListGroup>
                      {queuedChats.map((conversation) => (
                        <ListGroupItem key={conversation.id}>
                          <Row className="align-items-center">
                            <Col md={8}>
                              <h6 className="mb-1">
                                {conversation.customer_name || 'Anonymous Customer'}
                              </h6>
                              <p className="text-muted small mb-1">
                                <i className="mdi mdi-email"></i> {conversation.customer_email || 'N/A'}
                              </p>
                              <p className="text-muted small mb-1">
                                <i className="mdi mdi-domain"></i> {conversation.site?.name || `Site #${conversation.site_id}`}
                              </p>
                              <small className="text-muted">
                                <i className="mdi mdi-clock-outline"></i> Waiting for {moment(conversation.created_at).fromNow(true)}
                              </small>
                            </Col>
                            <Col md={4} className="text-end">
                              <Badge color="warning" className="mb-2 d-block">
                                {conversation.status}
                              </Badge>
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() => handleTakeConversation(conversation.id)}
                                disabled={takingId === conversation.id || !isConnected}
                              >
                                {takingId === conversation.id ? (
                                  <>
                                    <Spinner size="sm" className="me-1" />
                                    Taking...
                                  </>
                                ) : (
                                  <>
                                    <i className="mdi mdi-account-check"></i> Take Conversation
                                  </>
                                )}
                              </Button>
                            </Col>
                          </Row>
                        </ListGroupItem>
                      ))}
                    </ListGroup>
                  </>
                ) : (
                  <div className="text-center text-muted py-5">
                    <i className="mdi mdi-check-circle-outline" style={{ fontSize: '64px', color: '#4caf50' }}></i>
                    <h5 className="mt-3">Queue is empty!</h5>
                    <p>All conversations have been assigned to agents.</p>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default QueueView
